import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore,
  Edit,
  Delete,
  Add,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { TrainingTopicDto, TrainingContentDto } from '../../../api/types/dtos/training';
import {
  useTrainingContents,
  useUpdateTrainingContent,
  useDeleteTrainingContent,
} from '../../../api/hooks/useTrainingContents';
import {
  useDeleteTrainingTopic,
} from '../../../api/hooks/useTrainingTopics';
import { useOrganization } from '../../../contexts/OrganizationContext';
import ContentCard from './ContentCard';
import SequenceControls from './SequenceControls';
import EditTopicModal from '../modals/EditTopicModal';
import EditContentModal from '../modals/EditContentModal';
import CreateContentModal from '../modals/CreateContentModal';
import { canMoveUp, canMoveDown, moveUp, moveDown } from '../utils/sequenceHelpers';

interface TopicAccordionProps {
  topic: TrainingTopicDto;
  allTopics: TrainingTopicDto[];
  trainingId: string;
  moduleId: string;
  onMoveUp: (topic: TrainingTopicDto) => void;
  onMoveDown: (topic: TrainingTopicDto) => void;
}

const TopicAccordion: React.FC<TopicAccordionProps> = ({
  topic,
  allTopics,
  trainingId,
  moduleId,
  onMoveUp,
  onMoveDown,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingContent, setEditingContent] = useState<TrainingContentDto | null>(null);
  const [createContentModalOpen, setCreateContentModalOpen] = useState(false);
  
  const { selectedOrganization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();

  const { data: contents = [], isLoading: contentsLoading } = useTrainingContents(
    selectedOrganization?.id || '',
    trainingId,
    moduleId,
    topic.id,
    { isActive: true }
  );

  const deleteTopic = useDeleteTrainingTopic(
    selectedOrganization?.id || '',
    trainingId,
    moduleId
  );

  const updateContent = useUpdateTrainingContent(
    selectedOrganization?.id || '',
    trainingId,
    moduleId,
    topic.id
  );

  const deleteContent = useDeleteTrainingContent(
    selectedOrganization?.id || '',
    trainingId,
    moduleId,
    topic.id
  );

  const handleDeleteTopic = async () => {
    try {
      await deleteTopic.mutateAsync(topic.id);
      enqueueSnackbar('Tema eliminado exitosamente', { variant: 'success' });
      setDeleteDialogOpen(false);
    } catch (error) {
      enqueueSnackbar('Error al eliminar el tema', { variant: 'error' });
      console.error('Error deleting topic:', error);
    }
  };

  const handleMoveContentUp = async (content: TrainingContentDto) => {
    const result = moveUp(content, contents);
    if (!result) return;

    try {
      await Promise.all([
        updateContent.mutateAsync({
          id: result.item.id,
          data: {
            name: result.item.name,
            description: result.item.description,
            sequence: result.item.sequence,
            url: result.item.url,
            durationMinutes: result.item.durationMinutes,
            progressContribution: result.item.progressContribution,
            topicId: result.item.topicId,
            typeId: result.item.typeId,
            isActive: result.item.isActive,
          },
        }),
        ...(result.swapItem
          ? [
              updateContent.mutateAsync({
                id: result.swapItem.id,
                data: {
                  name: result.swapItem.name,
                  description: result.swapItem.description,
                  sequence: result.swapItem.sequence,
                  url: result.swapItem.url,
                  durationMinutes: result.swapItem.durationMinutes,
                  progressContribution: result.swapItem.progressContribution,
                  topicId: result.swapItem.topicId,
                  typeId: result.swapItem.typeId,
                  isActive: result.swapItem.isActive,
                },
              }),
            ]
          : []),
      ]);
      enqueueSnackbar('Orden actualizado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error al actualizar el orden', { variant: 'error' });
    }
  };

  const handleMoveContentDown = async (content: TrainingContentDto) => {
    const result = moveDown(content, contents);
    if (!result) return;

    try {
      await Promise.all([
        updateContent.mutateAsync({
          id: result.item.id,
          data: {
            name: result.item.name,
            description: result.item.description,
            sequence: result.item.sequence,
            url: result.item.url,
            durationMinutes: result.item.durationMinutes,
            progressContribution: result.item.progressContribution,
            topicId: result.item.topicId,
            typeId: result.item.typeId,
            isActive: result.item.isActive,
          },
        }),
        ...(result.swapItem
          ? [
              updateContent.mutateAsync({
                id: result.swapItem.id,
                data: {
                  name: result.swapItem.name,
                  description: result.swapItem.description,
                  sequence: result.swapItem.sequence,
                  url: result.swapItem.url,
                  durationMinutes: result.swapItem.durationMinutes,
                  progressContribution: result.swapItem.progressContribution,
                  topicId: result.swapItem.topicId,
                  typeId: result.swapItem.typeId,
                  isActive: result.swapItem.isActive,
                },
              }),
            ]
          : []),
      ]);
      enqueueSnackbar('Orden actualizado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error al actualizar el orden', { variant: 'error' });
    }
  };

  const handleDeleteContent = async (contentId: string) => {
    try {
      await deleteContent.mutateAsync(contentId);
      enqueueSnackbar('Contenido eliminado exitosamente', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error al eliminar el contenido', { variant: 'error' });
      console.error('Error deleting content:', error);
    }
  };

  const sortedContents = [...contents].sort((a, b) => a.sequence - b.sequence);

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{
          mb: 1,
          border: '1px solid',
          borderColor: 'divider',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              justifyContent: 'space-between',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <Typography variant="subtitle1">{topic.name}</Typography>
            <Chip
              label={`Seq ${topic.sequence}`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${topic.progressContribution}%`}
              size="small"
              color="secondary"
              variant="outlined"
            />
            <Chip
              label={`${contents.length} contenidos`}
              size="small"
              color="info"
              variant="outlined"
            />
          </Box>
          <Box
            sx={{ display: 'flex', gap: 0.5 }}
            onClick={(e) => e.stopPropagation()}
          >
            <SequenceControls
              canMoveUp={canMoveUp(topic, allTopics)}
              canMoveDown={canMoveDown(topic, allTopics)}
              onMoveUp={() => onMoveUp(topic)}
              onMoveDown={() => onMoveDown(topic)}
            />
            <Tooltip title="Editar">
              <IconButton
                size="small"
                onClick={() => setEditModalOpen(true)}
                color="primary"
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar">
              <IconButton
                size="small"
                onClick={() => setDeleteDialogOpen(true)}
                color="error"
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {topic.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {topic.description}
            </Typography>
          )}

          {contentsLoading ? (
            <Typography>Cargando contenidos...</Typography>
          ) : (
            <>
              {sortedContents.map((content) => (
                <ContentCard
                  key={content.id}
                  content={content}
                  allContents={sortedContents}
                  onEdit={setEditingContent}
                  onDelete={handleDeleteContent}
                  onMoveUp={handleMoveContentUp}
                  onMoveDown={handleMoveContentDown}
                />
              ))}

              <Button
                variant="outlined"
                startIcon={<Add />}
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => setCreateContentModalOpen(true)}
              >
                Agregar Contenido
              </Button>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      <EditTopicModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        topic={topic}
        trainingId={trainingId}
        moduleId={moduleId}
      />

      <EditContentModal
        open={!!editingContent}
        onClose={() => setEditingContent(null)}
        content={editingContent}
        trainingId={trainingId}
        moduleId={moduleId}
        topicId={topic.id}
      />

      <CreateContentModal
        open={createContentModalOpen}
        onClose={() => setCreateContentModalOpen(false)}
        trainingId={trainingId}
        moduleId={moduleId}
        topicId={topic.id}
        existingContents={contents}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea eliminar el tema &quot;{topic.name}&quot;? Esto también eliminará
            todos los contenidos asociados. Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDeleteTopic} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TopicAccordion;

